// HarmonicSeriesCell.tsx
// Roster column: fundamental CPM where the harmonic series begins,
// colored by HARMONIC-ENERGY rate-of-change (per machine, ~30d), gated to
// running captures only. Number = where; color = is the comb moving.
//
// Payload comes from compute_harmonic_column() (backend):
//   { state, fundamental_cpm, roc_state, roc_pct_wk, r2, energy_ratio, n_captures }

type RocState = "rising" | "flat" | "falling" | "insufficient" | "none";
type CellState = "ok" | "no_comb" | "no_data";

export interface HarmonicCellData {
  state: CellState;
  fundamental_cpm: number | null;
  roc_state: RocState;
  roc_pct_wk: number | null;
  r2: number | null;
  energy_ratio: number | null;
  n_captures: number;
  axis?: string;
}

// color carries ROC only (single channel). rising = hot, flat = neutral,
// falling = amber + down-caret (ambiguous late-stage; deliberately NOT green).
const ROC_STYLE: Record<RocState, { text: string; caret?: "up" | "down" }> = {
  rising:       { text: "text-red-600 font-semibold", caret: "up" },
  falling:      { text: "text-amber-600 font-medium", caret: "down" },
  flat:         { text: "text-gray-700" },
  insufficient: { text: "text-gray-400" },
  none:         { text: "text-gray-400" },
};

function fmtCpm(n: number) {
  return `${Math.round(n).toLocaleString()} CPM`;
}

export function HarmonicSeriesCell({ data }: { data: HarmonicCellData }) {
  // --- three distinct empty states (never collapse to one dash) ---
  if (data.state === "no_data") {
    return (
      <span className="text-gray-300" title="No running captures (all below 0.1 g)">
        —
      </span>
    );
  }
  if (data.state === "no_comb") {
    return (
      <span className="text-gray-400" title="Running, but no harmonic series detected">
        no comb
      </span>
    );
  }

  const cpm = data.fundamental_cpm!;
  const style = ROC_STYLE[data.roc_state];

  // roc_pct_wk is null when roc_state='flat' (r² < threshold — poor fit, neutral)
  const tip =
    data.roc_state === "insufficient"
      ? `Series at ${fmtCpm(cpm)} · building trend history (${data.n_captures} captures)`
      : data.roc_pct_wk != null
        ? `Series at ${fmtCpm(cpm)} · harmonic energy ${
            data.roc_pct_wk >= 0 ? "+" : ""
          }${data.roc_pct_wk}%/wk (r²=${data.r2})${
            data.energy_ratio != null ? ` · ${data.energy_ratio}× baseline` : ""
          }${data.roc_state === "falling" ? " · falling (verify: late-stage or improving)" : ""}`
        : `Series at ${fmtCpm(cpm)} · harmonic energy stable${
            data.energy_ratio != null ? ` · ${data.energy_ratio}× baseline` : ""
          }`;

  return (
    <div className="flex flex-col gap-0.5" title={tip}>
      <div className="flex items-center gap-1">
        <span className={style.text}>{fmtCpm(cpm)}</span>
        {style.caret === "up" && <span className="text-red-600 text-xs">▲</span>}
        {style.caret === "down" && <span className="text-amber-600 text-xs">▼</span>}
      </div>

      {/* optional 2nd channel: energy vs this machine's own baseline (bar + ratio label) */}
      {data.energy_ratio != null && (
        <div className="flex items-center gap-1.5">
          <div className="h-1 w-14 rounded bg-gray-100">
            <div
              className="h-1 rounded bg-gray-400"
              style={{ width: `${Math.min(100, data.energy_ratio * 50)}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-400 leading-none">{data.energy_ratio}×</span>
        </div>
      )}
    </div>
  );
}
